import { gql, useMutation } from '@apollo/client';
import {
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import Select, { OptionProps } from 'react-select';
import { ModalCloseButton } from '~components/ui/ModalCloseButton';
import { components } from '~components/ui/Select';
import {
  ConfigurationStatus,
  DefaultConfigurationMap,
  DefaultConfigurationType,
} from '~graphql/__generated__/types';
import { ensure } from '~utils/types';

interface Props {
  isOpen: boolean;
  onCancel: () => void;
  onSuccess?: (id: string) => Promise<void> | void;
  defaultConfigurationType: DefaultConfigurationType;
  multiClaimId?: string;
  supportedPlatforms: string[];
  saveHandler?: (configs: DefaultConfigurationMap[]) => void;
  defaultConfigurations: DefaultConfigurationMap[];
  configurations: Array<{
    id: string;
    name: string;
    platform: string;
    state: ConfigurationStatus;
  }>;
}

export const SetDefaultConfigurationModal = ({
  isOpen,
  onCancel,
  onSuccess,
  defaultConfigurationType,
  configurations,
  saveHandler,
  defaultConfigurations,
  supportedPlatforms,
}: Props) => {
  const { t } = useTranslation();
  const toast = useToast();

  const { customer } = useParams();
  const customerHandle = ensure(customer);

  const ensureThreeRows = (rows: Array<{ platform?: string; configurationId?: string }>) => {
    const copy = [...rows];
    while (copy.length < 3) {
      copy.push({ platform: undefined, configurationId: undefined });
    }
    return copy.slice(0, 3);
  };

  const [rows, setRows] = useState<Array<{ platform?: string; configurationId?: string }>>(
    ensureThreeRows(defaultConfigurations),
  );

  const [updateDefaultConfiguration, { loading: saving }] = useMutation(
    SetDefaultConfigurationModal.graphql.mutations.SetDefaultConfiguration,
  );
  const usedPlatforms = rows.map((r) => r.platform).filter(Boolean);

  const getAvailablePlatforms = (currentPlatform?: string) => {
    const platforms = supportedPlatforms.filter(
      (p: string) => !usedPlatforms.includes(p) || p === currentPlatform,
    );
    return platforms.sort((a: string, b: string) => a.localeCompare(b));
  };

  const updateRow = (index: number, key: 'platform' | 'configurationId', value?: string) => {
    setRows((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [key]: value };

      // reset configuration if platform changes
      if (key === 'platform') {
        copy[index].configurationId = undefined;
      }

      return copy;
    });
  };

  const handleSave = async () => {
    try {
      const filteredRows = rows.filter((r) => r.platform && r.configurationId);
      if (defaultConfigurationType === DefaultConfigurationType.Multiclaim) {
        saveHandler && saveHandler(filteredRows as DefaultConfigurationMap[]);
      } else {
        await updateDefaultConfiguration({
          variables: {
            input: {
              customerHandle,
              type: defaultConfigurationType,
              multiClaimId: undefined,
              defaultConfigurations: filteredRows.map((r) => ({
                platform: r.platform,
                configurationId: r.configurationId,
              })),
            },
          },
        });
        if (onSuccess) await onSuccess(customerHandle);
      }
      onCancel();
      toast({
        title: t('defaultConfiguration'),
        status: 'success',
        description: t('defaultConfigurationUpdateSuccess'),
      });
    } catch (e) {
      toast({
        title: t('defaultConfiguration'),
        status: 'error',
        description: t('defaultConfigurationUpdateFailed'),
      });
    }
  };

  type PlatformSelectOption = {
    label: string;
    value: string;
  };

  function PlatformSelectOptionComponent({
    isSelected,
    innerProps,
    ...rest
  }: OptionProps<PlatformSelectOption, false>) {
    const data = rest.data;
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '8px 14px',
          cursor: 'pointer',
          color: isSelected ? '#fff' : '#2A4365',
          background: isSelected ? '#4299E1' : '#fff',
          fontSize: '16px',
        }}
        {...innerProps}
      >
        <span style={{ flex: 1 }}>{data.label}</span>
      </div>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onCancel} size={'4xl'}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('setDefaultConfiguration')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={8} align="stretch">
            {defaultConfigurationType === DefaultConfigurationType.Customer ? (
              <span>{t('setDefaultConfigurationCustomerDesc')}</span>
            ) : (
              <span>{t('setDefaultConfigurationBulkClaimDesc')}</span>
            )}
            {ensureThreeRows(rows).map((row, idx) => {
              // Platform options for react-select
              const platforms = getAvailablePlatforms(row.platform).map((p) => ({
                label: p,
                value: p,
              }));
              // Configuration options for react-select
              const configurationOptions = row.platform
                ? configurations
                    .filter(
                      (c) =>
                        c.platform === row.platform && c.state === ConfigurationStatus.Published,
                    )
                    .map((c) => ({ label: c.name, value: c.id }))
                : [];
              return (
                <HStack key={idx} spacing={4} align="end">
                  <VStack align="start" spacing={3} flex={1} style={{ width: '40%' }}>
                    <span style={{ fontWeight: 'bold' }}>{t('selectDisplayPlatform')}</span>
                    <Select
                      value={platforms.find((opt) => opt.value === row.platform) || null}
                      options={platforms}
                      components={{ ...components, Option: PlatformSelectOptionComponent }}
                      onChange={(selected) => updateRow(idx, 'platform', selected?.value)}
                      isDisabled={saving}
                      placeholder={t('selectPlatform')}
                      isClearable={true}
                      isSearchable={true}
                      menuPlacement="auto"
                      styles={{ container: (base) => ({ ...base, width: '100%' }) }}
                    />
                  </VStack>
                  <VStack align="start" spacing={3} flex={1} style={{ width: '60%' }}>
                    <span style={{ fontWeight: 'bold' }}>{t('selectConfiguration')}</span>
                    <Select
                      value={
                        configurationOptions.find((opt) => opt.value === row.configurationId) ||
                        null
                      }
                      options={configurationOptions}
                      onChange={(selected) => updateRow(idx, 'configurationId', selected?.value)}
                      isDisabled={!row.platform}
                      placeholder={t('selectConfiguration')}
                      isClearable={true}
                      isSearchable={true}
                      menuPlacement="auto"
                      styles={{ container: (base) => ({ ...base, width: '100%' }) }}
                    />
                  </VStack>
                </HStack>
              );
            })}
          </VStack>
          <HStack justify="flex-end" mt={8}>
            <Button onClick={onCancel} variant="outline">
              {t('cancel')}
            </Button>
            <Button onClick={handleSave} colorScheme="blue" isLoading={saving}>
              {t('save')}
            </Button>
          </HStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

SetDefaultConfigurationModal.graphql = {
  queries: {
    SetDefaultConfigurationModal: gql`
      query SetDefaultConfigurationModal($customerHandle: String!) {
        customer: customerByHandle(handle: $customerHandle) {
          id
          supportedPlatforms
          customerDefaultConfigurations {
            id
            type
            defaultConfigurations {
              platform
              configurationId
            }
          }
        }
      }
    `,
  },
  mutations: {
    SetDefaultConfiguration: gql`
      mutation UpdateDefaultConfiguration($input: UpdateDefaultConfigurationInput!) {
        updateCustomerDefaultConfiguration(input: $input) {
          id
          type
          defaultConfigurations {
            platform
            configurationId
          }
        }
      }
    `,
  },
};
